/* eslint-disable import/order */
/* eslint-disable no-console */
import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import ROUTES from './routes';

import { Footer, FooterComplex, Splash } from '@/components';
import useAuth from '@/hooks/useAuth';
import useCountMedals from '@/hooks/useCountMedals';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import {
  getAccountInitialDataViaOTP,
  setShouldCheckLoggedInNative,
} from '@/services';
import { TEXTS } from '@/utils';

import {
  processInitialDataEstadoOk,
  shouldHideFooter,
  shouldShowMinimalFooter,
} from './AuthenticatedNativo';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';

/**
 * AuthenticatedOTP
 * <br/>
 * Login vía token de "One time password".
 * <br/>
 * Interactua con useAuth via "setStateLoginViaOTP".
 * Setea "setShouldCheckLoggedInNative", en sessionStorage.
 * <br/>
 * Ver useAuth para más detalles.
 * @see {useAuth}
 * @param hashLogin token (de url) para login.
 */
export const AuthenticatedOTP = ({ hashLogin }: { hashLogin?: string }) => {
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { show } = useToast();
  const { handleSetTournamentMaps } = useTournament();
  const { handleSetPinsData, pinsData } = usePinsAccount();
  const { handleSetMedalsData } = useCountMedals();

  const {
    isAuthenticated,
    logout,
    setUserInitialData,
    // isLoading: authHookLoading,
    setStateLoginViaOTP,
  } = useAuth();

  const { setTshirtAndShield } = useTshirtAndShield();
  const location = useLocation();

  const handleRedirectAfterLogin = useCallback(async () => {
    setUserInitialData(null);

    if (isAuthenticated) {
      setLoading(false);
      return;
    }

    if (!hashLogin) {
      return;
    }

    try {
      setStateLoginViaOTP({
        isLoggedInViaHash: false,
        isLoading: true,
        isUsingLoginViaHash: true,
      });
      const result = await getAccountInitialDataViaOTP({ hashLogin });

      if (result && result.estado) {
        setStateLoginViaOTP({
          isLoggedInViaHash: true,
          isLoading: false,
          isUsingLoginViaHash: true,
        });
        setShouldCheckLoggedInNative(true);
        await processInitialDataEstadoOk(
          result,
          setUserInitialData,
          setTshirtAndShield,
          handleSetTournamentMaps,
          handleSetPinsData,
          handleSetMedalsData,
          navigate,
          location
        );
        setLoading(false);
      } else {
        setShouldCheckLoggedInNative(false);
        setStateLoginViaOTP({
          isLoggedInViaHash: false,
          isLoading: false,
          isUsingLoginViaHash: false,
        });
        setUserInitialData(null);
        console.log('2-otp');
        show(TEXTS.defaultError);
        logout();
        setLoading(false);
      }
    } catch (error) {
      const err = error as Error;
      show(err.message);
      navigate(ROUTES.notFound, {
        replace: true,
      });
      setLoading(false);
    }
    return () => {
      console.log('unmounted AuthenticatedOTP?');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashLogin]); // [authHookLoading, isAuthenticated]);

  useEffect(() => {
    handleRedirectAfterLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashLogin]);

  if (isLoading) {
    return <Splash />;
  }

  // oculto el footer para urls determinadas
  const showMinimalFooter =
    shouldShowMinimalFooter(window.location.pathname) ||
    (!isAuthenticated &&
      window.location.pathname === ROUTES.preguntasFrecuentes);

  // oculto el footer para urls determinadas
  const hideFooter = shouldHideFooter(window.location.pathname);

  return (
    <>
      <Outlet />
      {!hideFooter &&
        (showMinimalFooter ? (
          <Footer />
        ) : (
          <FooterComplex isPremium={pinsData.isPremium} />
        ))}
    </>
  );
};

AuthenticatedOTP.displayName = 'AuthenticatedOTP';

export default AuthenticatedOTP;
